import { useMutation, useQueryClient } from "@tanstack/react-query";
import post from "@adora/shared/utils/post";
import { JsonValue } from "@adora/shared/utils/patch";
import { RelativeUrl } from "@adora/shared/utils/environment";
import { snakeToCamel } from "@adora/shared/utils/case-conversions";
import { queryKeys } from "../utils/tanstack-query-helpers";
import type {
  AdjustTokensRequest,
  AdjustTokensResponse,
  CreateWalletRequest,
  TokenWallet,
  WalletServerResponse
} from "../types/token-wallet";

const cacheWallet = (wallet: WalletServerResponse): TokenWallet => snakeToCamel(wallet);

/** Creates a wallet for a brand (POST /tokens/wallet). */
export function useCreateWallet(brandId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateWalletRequest) => {
      const result = await post<WalletServerResponse>(
        `${RelativeUrl}/tokens/wallet`,
        {},
        request as unknown as JsonValue
      );
      return result.match(
        (error) => {
          throw new Error(error.message);
        },
        (data) => data
      );
    },
    onSuccess: (data) => {
      // Key by the response's own wallet_type (not a captured value) so a wallet
      // type switched mid-flight can't write into the wrong cache entry.
      const key = queryKeys.tokenWallet(brandId, data.wallet_type);
      // Seed the cache immediately so the page reflects the new wallet without a
      // refetch flash, then revalidate in the background.
      queryClient.setQueryData(key, cacheWallet(data));
      queryClient.invalidateQueries({ queryKey: key });
    }
  });
}

/** Adjusts an existing wallet (POST /tokens/adjust). */
export function useAdjustTokens(brandId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AdjustTokensRequest) => {
      const result = await post<AdjustTokensResponse>(
        `${RelativeUrl}/tokens/adjust`,
        {},
        request as unknown as JsonValue
      );
      return result.match(
        (error) => {
          throw new Error(error.message);
        },
        (data) => data
      );
    },
    onSuccess: (data) => {
      // The adjust response carries the updated wallet — write it through (keyed
      // by that wallet's own type, robust to a mid-flight type switch), then
      // revalidate.
      const key = queryKeys.tokenWallet(brandId, data.wallet.wallet_type);
      queryClient.setQueryData(key, cacheWallet(data.wallet));
      queryClient.invalidateQueries({ queryKey: key });
    }
  });
}
